import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ManualDialParams } from '../dialer/models/manual-dial-params.model';
import { DialerService } from '../dialer/services/dialer.service';
import { ConversationService } from '../conversations/services/conversation.service';
import { DncCheckService } from '../admin/dialer-config/services/dnc-check.service';
import { CallControlPanelActionBarService } from '../dialer/services/call-control-panel-action-bar.service';
import { DialerEventType } from '../dialer/models/dialer-event.model';
import Swal from 'sweetalert2';

@Component({
    selector: 'dial-number-and-call-history',
    templateUrl: 'dial-number-and-call-history.component.html',
    styleUrls: ['dial-number-and-call-history.component.scss'],
    standalone: false
})
export class DialNumberAndCallHistoryComponent implements OnInit, OnDestroy {

  @Input()
  panelType: 'classic' | 'twilio' | 'ringCentral' = 'classic';

  @Input()
  showCallControls: boolean = true;

  @Input()
  showDialpad: boolean = true;

  @Output()
  closeDialClicked = new EventEmitter<any>();

  @ViewChild('quickSmsForm') quickSmsForm: NgForm | undefined;

  protected numberToCall: string;
  protected activeTab: 'dialpad' | 'active-call' = 'dialpad';
  protected quickNotesExpanded: boolean = false;
  protected quickSmsExpanded: boolean = false;

  private _smsEventSubscription: Subscription;

  constructor(
    private readonly _dialerService: DialerService,
    private readonly _conversationService: ConversationService,
    private readonly _dncCheckService: DncCheckService,
    public readonly callControlService: CallControlPanelActionBarService
  ) {
  }

  ngOnInit(): void {
    this._smsEventSubscription = this._dialerService.events.subscribe(event => {
      if (event?.eventType === DialerEventType.quickSmsSent) {
        this.resetSmsForm();
      }
    });
  }

  ngOnDestroy(): void {
    if (this._smsEventSubscription) {
      this._smsEventSubscription.unsubscribe();
    }
  }

  onCallClicked = () => {
    if (!(this.numberToCall && this.numberToCall.length === 10)) {
      return;
    }

    this._dncCheckService.checkPhoneNumber(this.numberToCall).subscribe({
      next: (result) => {
        if (result.isOnDncList) {
          Swal.fire({
            title: 'Do Not Call Record',
            text: `You can't call this number because it is on the DNC list!`,
            icon: 'warning',
            showCancelButton: false,
            confirmButtonText: 'OK',
            reverseButtons: true
          });
          return;
        }

        let data = {
          phoneNumber: this.numberToCall,
          phoneType: 'mobile',
        } as ManualDialParams;

        this._dialerService.openCallControlPanel(data);
      }
    });
  }

  onSmsClicked = () => {
    if (!(this.numberToCall && this.numberToCall.length === 10)) {
      return;
    }

    this._dncCheckService.checkPhoneNumber(this.numberToCall).subscribe({
      next: (result) => {
        if (result.isOnDncList) {
          Swal.fire({
            title: 'Do Not Call Record',
            text: `You can't text this number because it is on the DNC list!`,
            icon: 'warning',
            showCancelButton: false,
            confirmButtonText: 'OK',
            reverseButtons: true
          });
          return;
        }

        this._conversationService.openSmsChat({
          userPhone: this.numberToCall,
          userName: "",
          openDrawer: true,
          openChat: true,
          conversationTargetPersonContext: {
            applicationId: null,
            borrowerId: null,
            leadId: null,
            agentId: null
          }
        });
      }
    });
  }

  onDialpadNumberClicked = (number: string) => {
    if (this.numberToCall && this.numberToCall.length === 10) {
      return;
    }
    this.numberToCall = this.numberToCall ? this.numberToCall + number : number;
  }

  onClearNumberClicked = () => {
    this.numberToCall = '';
  }

  onDialpadBackspaceClicked = () => {
    this.numberToCall = this.numberToCall.slice(0, -1);
  }

  onCloseCallHistoryClicked = () => {
    this.numberToCall = '';
    this.closeDialClicked.emit();
  }

  onSendSmsClicked = () => {
    if (this.quickSmsForm) {
      this.quickSmsForm.form.markAllAsTouched();

      if (this.quickSmsForm.form.valid) {
        this.callControlService.sending = true;
        this.callControlService.sendSms();
      }
    }
  }

  private resetSmsForm(): void {
    if (this.quickSmsForm) {
      this.quickSmsForm.form.markAsPristine();
      this.quickSmsForm.form.markAsUntouched();
    }
  }

  toggleQuickNotes(): void {
    this.quickNotesExpanded = !this.quickNotesExpanded;
    if (this.quickNotesExpanded) {
      this.quickSmsExpanded = false;
    }
  }

  toggleQuickSms(): void {
    this.quickSmsExpanded = !this.quickSmsExpanded;
    if (this.quickSmsExpanded) {
      this.quickNotesExpanded = false;
    }
  }
}
